import React, { useEffect, useState } from "react";
import { Button, Layout, message, Modal } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import { Role_Management, Role_Account_Query } from "../../services/userAPI"; // Ensure correct path
import "./viewRole.css";
import Clan from "../../Images/clan-logo.png";
import Darts from "../../Images/Darts-Logo 1.png";
import Lense from "../../Images/Lense-Logo 1.png";
import { useLocation, useNavigate } from "react-router-dom";
import PageBreadcrumb from '../../reusableField/PageBreadcrumb';

const ViewRoles = () => {
    const { Content } = Layout;
    const location = useLocation();
    const navigate = useNavigate();
    const roleId = location.state?.roleId;
    const accountId = location.state?.accountId;
    const accountName = location.state?.accountName;
    const name = location.state?.name;

    // State for product access
    const [productAccess, setProductAccess] = useState({
        DARTS: location.state?.dartsNeeded ?? false,
        CLAN: location.state?.clanNeeded ?? false,
        SYNC: location.state?.syncNeeded ?? false,
        LENSE: location.state?.lenseNeeded ?? false,
    });
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updatingProduct, setUpdatingProduct] = useState(null);

    // Fetch job role data using Role_Account_Query
    const { loading: roleLoading, error: roleError, data: roleData, refetch } = useQuery(Role_Account_Query, {
        variables: { accountId: parseInt(accountId) },
        fetchPolicy: "network-only",
        skip: !accountId, // Skip query if accountId is not available
    });

    useEffect(() => {
        if (accountId && roleId) {
            refetch(); // Refetch the query when the component mounts
        }
    }, [accountId, roleId, refetch]);

    // Update productAccess state when roleData is fetched
    useEffect(() => {
        if (roleData?.getAllJobrole) {
            const jobRole = roleData.getAllJobrole.find((role) => role.id === roleId);
            if (jobRole) {
                setProductAccess({
                    DARTS: jobRole.dartsNeeded ?? location.state?.dartsNeeded ?? false,
                    CLAN: jobRole.clanNeeded ?? location.state?.clanNeeded ?? false,
                    SYNC: jobRole.syncNeeded ?? location.state?.syncNeeded ?? false,
                    LENSE: jobRole.lenseNeeded ?? location.state?.lenseNeeded ?? false,
                });
            }
        }
    }, [roleData, roleId, location.state]);

    const [addJobrole, { loading: addRoleLoading }] = useMutation(Role_Management);

    const handleUpdateAccess = async () => {
        const currentRole = roleData?.getAllJobrole?.find(r => r.id === roleId);
        if (!currentRole) {
            message.error("Role data not available.");
            return;
        }

        // 1. Optimistically update UI
        const newAccess = !productAccess[selectedProduct];
        setProductAccess(prev => ({ ...prev, [selectedProduct]: newAccess }));
        setUpdatingProduct(selectedProduct);          // <-- show spinner on card
        setShowAccessModal(false);

        const variables = {
            accountId: parseInt(accountId),
            jobroleId: roleId,
            name: currentRole.name,

            clanNeeded: selectedProduct === "CLAN" ? newAccess : productAccess.CLAN,
            syncNeeded: selectedProduct === "SYNC" ? newAccess : productAccess.SYNC,
            dartsNeeded: selectedProduct === "DARTS" ? newAccess : productAccess.DARTS,
            lenseNeeded: selectedProduct === "LENSE" ? newAccess : productAccess.LENSE,

            // all other required fields (unchanged)
            dartsFormNeeded: currentRole.dartsFormNeeded ?? false,
            pocName: currentRole.pocName || "",
            pocEmail: currentRole.pocEmail || "",
            pocNumber: currentRole.pocContactNumber || "",
            numberLogin: currentRole.numberLogin ?? false,
            emailLogin: currentRole.emailLogin ?? false,
            mandatoryTimeDistribution: currentRole.mandatoryTimeDistribution ?? false,
            signInSignOutRequired: currentRole.signInSignOutRequired ?? false,
            locationRequired: currentRole.locationRequired ?? false,
            targetEditDays: currentRole.targetEditDays ?? 0,
            inputEditDays: currentRole.inputEditDays ?? 0,
            ldmsNeeded: currentRole.ldmsNeeded ?? false,
            mirrorNeeded: currentRole.mirrorNeeded ?? false,
            jobroleAccessUserIds: currentRole.jobroleAccessUsers?.map(u => u.id) || [],
        };

        try {
            await addJobrole({ variables });
            message.success(`${selectedProduct} access updated`);
            await refetch(); // <-- wait for fresh data
        } catch (err) {
            // 2. Revert on error
            setProductAccess(prev => ({ ...prev, [selectedProduct]: !newAccess }));
            message.error("Update failed: " + err.message);
        } finally {
            setUpdatingProduct(null);
        }
    };

    const products = [
        { name: "LENSE", tagline: "DISCOVER THE UNSEEN", logo: Lense, needed: productAccess.LENSE, route: "/lense-management" },
        { name: "DARTS", tagline: "MAKE EVERYDAY COUNT", logo: Darts, needed: productAccess.DARTS, route: "/productConfiguration" },
        { name: "CLAN", tagline: "WIN AT WORK", logo: Clan, needed: productAccess.CLAN, route: "/clan-management" },
    ];

    const handleProductClick = (product) => {
        if (productAccess[product.name]) {
            navigate(product.route, {
                state: {
                    roleId,
                    accountId,
                    dartsFormNeeded: roleData?.getAllJobrole.find((role) => role.id === roleId)?.dartsFormNeeded ?? location.state?.dartsFormNeeded,
                    pocName: location.state?.pocName,
                    pocEmail: location.state?.pocEmail,
                    pocNumber: location.state?.pocNumber,
                    numberLogin: location.state?.numberLogin,
                    emailLogin: location.state?.emailLogin,
                    mandatoryTimeDistribution: location.state?.mandatoryTimeDistribution,
                    targetEditDays: location.state?.targetEditDays,
                    inputEditDays: location.state?.inputEditDays,
                    name,
                    dartsNeeded: productAccess.DARTS,
                    clanNeeded: productAccess.CLAN,
                    syncNeeded: productAccess.SYNC,
                    accountName,
                },
            });
        } else {
            setSelectedProduct(product.name);
            setShowAccessModal(true);
        }
    };

    const handleDiscardClick = () => {
        navigate("/admin");
    };

    return (
        <Content>
            <div className="product-selection-container">
                <PageBreadcrumb homePath="/admin">
                    <span onClick={() => navigate("/admin")} style={{ cursor: 'pointer' }}>{accountName}</span>
                    <span>&gt;</span>
                    <span>{name}</span>
                    <span>&gt;</span>
                    <span style={{ fontWeight: 'bold' }}>View Roles</span>
                </PageBreadcrumb>

                {roleLoading && <div>Loading...</div>}
                {roleError && <div>Error: {roleError.message}</div>}

                <h2 className="title">Select The Product To Configure</h2>
                <div className="product-grid">
                    {products.map((product, index) => (
                        <div
                            key={index}
                            className={`product-card ${productAccess[product.name] ? "active" : "disabled"} ${selectedProduct === product.name ? "selected" : ""
                                }`}
                            onClick={() => handleProductClick(product)}
                            style={{ cursor: "pointer" }}
                        >
                            <img src={product.logo} alt={product.name} className="product-logo" />
                        </div>
                    ))}
                </div>
                <br />

                <div className="user-management-and-role-management-header">
                    <button
                        className="userManagementRedirect"
                        onClick={() =>
                            navigate("/user-management", {
                                state: { accountId, roleId, accountName, name },
                            })
                        }
                    >
                        User Management
                    </button>
                    <button
                        className="roleManagementRedirect"
                        onClick={() =>
                            navigate("/role-management", {
                                state: { accountId, roleId, accountName, name },
                            })
                        }
                    >
                        Role Management
                    </button>
                </div>

                <Modal
                    title="Access Required"
                    open={showAccessModal}
                    onCancel={() => setShowAccessModal(false)}
                    footer={[
                        <button
                            key="no"
                            onClick={() => setShowAccessModal(false)}
                            className="cancel-button"
                            style={{ marginRight: "10px" }}
                        >
                            No
                        </button>,
                        <button
                            className="create-button"
                            key="yes"
                            type="primary"
                            onClick={handleUpdateAccess}
                            disabled={addRoleLoading || isUpdating}
                        >
                            {addRoleLoading || isUpdating ? "Updating..." : "Yes"}
                        </button>,
                    ]}
                >
                    <p>Do you need to give access to {selectedProduct}?</p>
                </Modal>
            </div>
        </Content>
    );
};

export default ViewRoles;